<template>
  <v-layout class="d-flex flex-column overflow-visible pl-1" style="border-bottom: 1px solid var(--light-3);">
    <v-row class="py-4 align-center flex-wrap">
      <v-col cols="1">
        <v-btn
          icon="mdi-drag-horizontal-variant"
          class="table-btn sortHandle"
          tabindex="-1"
          :disabled="$store.state.isWorkflowDisabled"
        />
      </v-col>
      <v-col cols="2" class="d-flex align-center justify-start gap-x-4">
        <workflow-settings-card-type-color-picker
          :color="cardType.color && cardType.color.rgb"
          variant="cards"
          @change="changeColor"
          :disabled="$store.state.isWorkflowDisabled"
        />
        <div class="d-flex flex-column">
          <quick-rename-input
            class="card-type-rename-input"
            :value="cardType.title"
            :disabled="$store.state.isWorkflowDisabled"
            @onChange="blurField($event)"
          />
          <div style="padding: 4px">
            <div v-if="!showDescription" class="show-hide-description text-light-blue expension-btn"
                 @click="openDescription">
              Show description
              <v-icon size="x-small" color="light-blue">chevron_right</v-icon>
            </div>
            <div v-else class="show-hide-description text-light-blue expension-btn" @click="closeDescription">
              Hide description
              <v-icon size="x-small" color="light-blue">chevron_right</v-icon>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Show in backlog -->
      <v-col cols="2" class="d-flex justify-center py-0">
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="d-flex align-center">
              <v-btn
                :icon="cardType.showInBacklog ? 'mdi-eye' : 'mdi-eye-off'"
                style="box-shadow: none; font-size: 16px"
                class="eye-btn justify-center ma-0 pa-0"
                @click="() => toggleShowInBacklog(!cardType.showInBacklog)"
                :disabled="$store.state.isWorkflowDisabled"
              ></v-btn>
            </div>
          </template>
          <span v-if="!$store.state.isWorkflowDisabled">View in backlog</span>
        </v-tooltip>
      </v-col>

      <!-- Use AI -->
      <v-col cols="1" class="d-flex justify-center py-0">
        <v-checkbox
          :model-value="cardType.generateWithAI"
          @update:model-value="saveGenerateCardWithAI(cardType)"
          hide-details
          :disabled="$store.state.isWorkflowDisabled"
        />
      </v-col>

      <v-col cols="5" class="d-flex justify-center py-0">
        <div class="w-50">
          <v-select
            :items="domainModelRoleItems"
            :model-value="domainModelRole"
            @update:model-value="updateDomainModelRole"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="$store.state.isWorkflowDisabled"
          />
        </div>
      </v-col>

      <v-col cols="1" class="d-flex justify-end py-0 pr-0">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props }">
            <span v-bind="props">
              <v-btn size="small" variant="text" icon class="ml-auto" @click="promptDeleteCardType(cardType)"
                     :disabled="$store.state.isWorkflowDisabled">
                <v-icon icon="mdi-trash-can-outline"></v-icon>
              </v-btn>
            </span>
          </template>
          <span>Delete type</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row class="my-0">
      <v-col class="pt-0 pb-0">
        <v-expand-transition>
          <div v-show="showDescription">
            <div class="card rounded-lg my-2 mr-2">
              <textarea
                v-model="descriptionModel"
                rows="2"
                class="card-description-textarea pa-2"
                ref="textareaElement"
                @focus="computeHeight"
                @keydown.exact="computeHeight" @keydown.enter.exact.prevent="submitDescriptionChange"
                @keydown.alt.enter.exact.prevent="handleDescriptionAltEnter"
                @keydown.27.stop="cancelDescriptionChange()"
                @cut="computeHeight" @paste="computeHeight" @drop="computeHeight"
                :disabled="$store.state.isWorkflowDisabled"
              />

              <v-layout justify-end class="pa-1 d-flex align-center">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon color="#61676E" size="20.5" v-bind="props">mdi-information-outline</v-icon>
                  </template>
                  <div style="width: 470px;">
                    Please provide a clear and concise description using standard terminology,
                    ensuring that you clearly state the key details about the new type to help
                    the AI generate accurate content
                  </div>
                </v-tooltip>

                <v-btn rounded="lg" size="small" variant="plain" class="ma-0 mx-2 action-button"
                       :disabled="descriptionModel === cardType.description" @click="cancelDescriptionChange">
                  Cancel
                </v-btn>

                <v-btn rounded="lg" size="small" variant="flat" color="primary" class="ma-0 action-button"
                       :disabled="descriptionModel === cardType.description" @click="submitDescriptionChange">Save
                </v-btn>
              </v-layout>
            </div>
          </div>
        </v-expand-transition>
      </v-col>
    </v-row>

    <v-dialog v-model="showDeleteDialog" max-width="440">
      <v-card class="pa-2">
        <v-card-title class="d-flex align-center text-red font-weight-bold">
          <v-icon color="red" class="mr-2">mdi-alert</v-icon>
          Delete Card Type
        </v-card-title>
        <v-card-text class="pt-2 px-3 pb-0">
          <div class="mb-2">
            Are you sure you want to delete the card type <strong>{{ typeToDelete?.title }}</strong>?
          </div>
          <div class="text-red font-weight-bold mb-2" style="font-size: 15px;">
            This operation cannot be undone.
          </div>
        </v-card-text>
        <v-card-actions class="py-1">
          <v-btn variant="tonal" @click="showDeleteDialog = false" :disabled="isDeletingType">Cancel</v-btn>
          <v-btn color="red" variant="tonal" @click="confirmDeleteType" :disabled="isDeletingType">
            <template v-if="isDeletingType">
              <v-progress-circular indeterminate color="white" size="18" class="mr-2"></v-progress-circular>
              Deleting...
            </template>
            <template v-else>
              Delete
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex'
import store from '@/store'
import QuickRenameInput from '@/components/QuickRenameInput.vue'
import WorkflowSettingsCardTypeColorPicker from '@/components/WorkflowSettingsCardTypeColorPicker'
import workflowApi from '@/api/workflowApi'
import cardTypesApi from '@/api/cardTypesApi'
import { DomainModelRole, DomainModelRoleLabels } from '@/constants/cardTypes'
import { stripHtml } from '@/utils/sanitize'

export default {
  components: {
    QuickRenameInput,
    WorkflowSettingsCardTypeColorPicker
  },

  props: {
    cardType: Object
  },

  data () {
    return {
      showDescription: false,
      descriptionModel: '',
      domainModelRoleItems: ['None', ...Object.values(DomainModelRoleLabels)],
      showDeleteDialog: false,
      typeToDelete: null,
      isDeletingType: false
    }
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow
    }),
    domainModelRole () {
      return this.getDomainModelRoleLabel(this.cardType.domainModelRole)
    }
  },

  methods: {
    getDomainModelRoleLabel (role) {
      if (!role) {
        return 'None'
      }

      return DomainModelRoleLabels[role] || 'None'
    },

    getDomainModelRoleFromLabel (label) {
      if (label === 'None') {
        return null
      }

      const entry = Object.entries(DomainModelRoleLabels).find(([, displayLabel]) => displayLabel === label)

      return entry ? entry[0] : null
    },
    blurField (event) {
      this.applyTitleChange(event)
    },
    async _emitUpdate (payload) {
      await cardTypesApi.updateCardType(this.$route.params.id, this.cardType, payload)
      await this.$store.dispatch('workflow/refreshCardTypes', { input: { workflowId: this.$route.params.id } })
    },

    applyTitleChange (ev) {
      const newTitle = ev.target.innerText
      const payload = { title: newTitle }
      if (newTitle !== this.cardType.title) {
        this._emitUpdate(payload)
      }
    },

    async changeColor (color) {
      await this._emitUpdate({ color })
      await store.dispatch('workflow/getWorkflow', this.$route.params)
    },

    toggleShowInBacklog (value) {
      const payload = {
        showInBacklog: Boolean(value)
      }
      this._emitUpdate(payload)
    },

    async updateDomainModelRole (newValue) {
      const newRole = this.getDomainModelRoleFromLabel(newValue)
      const oldRole = this.cardType.domainModelRole
      const isOldTagType = oldRole === DomainModelRole.TAG
      const isNewTagType = newRole === DomainModelRole.TAG

      if (isOldTagType !== isNewTagType) {
        const cardsToUpdate = this.findCardsWithThisCardType()

        if (cardsToUpdate.length > 0) {
          if (isOldTagType && !isNewTagType) {
            await this.convertTagsToDescription(cardsToUpdate)
          } else if (!isOldTagType && isNewTagType) {
            await this.convertDescriptionToTags(cardsToUpdate)
          }
        }
      }

      const payload = { domainModelRole: newRole }

      if (isNewTagType && !isOldTagType) {
        payload.description = 'Relevant tag to categorize and filter events in the workflow'
        payload.showInBacklog = false
      }

      this._emitUpdate(payload)
    },

    findCardsWithThisCardType () {
      const cards = []
      if (this.workflow?.domainEvents) {
        for (const event of this.workflow.domainEvents) {
          if (event.cards) {
            for (const card of event.cards) {
              if (card.cardTypeId === this.cardType.id) {
                cards.push(card)
              }
            }
          }
        }
      }
      return cards
    },

    async convertDescriptionToTags (cards) {
      const cardsToUpdate = []

      for (const card of cards) {
        const description = stripHtml(card.description || '')
        let tags = []

        try {
          const parsed = JSON.parse(description)
          if (Array.isArray(parsed)) {
            tags = parsed
          }
        } catch {
          // If description is not valid JSON, leave tags empty
        }

        cardsToUpdate.push({
          cardId: card.id,
          tags,
          description: '',
          version: card.version
        })
      }

      if (cardsToUpdate.length > 0) {
        await workflowApi.updateCardsMutation(this.$route.params.id, cardsToUpdate)
      }
    },

    async convertTagsToDescription (cards) {
      const cardsToUpdate = []

      for (const card of cards) {
        const tags = card.tags || []
        cardsToUpdate.push({
          cardId: card.id,
          description: tags.length > 0 ? JSON.stringify(tags) : '',
          tags: [],
          version: card.version
        })
      }

      if (cardsToUpdate.length > 0) {
        await workflowApi.updateCardsMutation(this.$route.params.id, cardsToUpdate)
      }
    },

    saveGenerateCardWithAI (cardType) {
      const payload = {
        generateWithAI: !cardType.generateWithAI
      }
      this._emitUpdate(payload)
    },

    computeHeight () {
      if (!this.$refs.textareaElement) {
        return
      }

      const { textareaElement } = this.$refs

      this.$nextTick(() => {
        setImmediate(() => {
          textareaElement.style.height = 'auto'
          textareaElement.style.height = `${textareaElement.scrollHeight}px`
        })
      })
    },

    openDescription () {
      this.descriptionModel = this.cardType.description || ''
      this.showDescription = true

      setTimeout(() => {
        if (this.$refs.textareaElement) {
          this.$refs.textareaElement.focus()
        }
      }, 50)
    },

    closeDescription () {
      this.showDescription = false
    },

    handleDescriptionAltEnter (event) {
      const { textareaElement } = this.$refs
      const startPosition = textareaElement.selectionStart
      const endPosition = textareaElement.selectionEnd
      // update textarea value
      this.descriptionModel = [
        this.descriptionModel.slice(0, startPosition),
        '\n',
        this.descriptionModel.slice(endPosition)
      ].join('')

      // set cursor position
      this.$nextTick(() => {
        textareaElement.selectionEnd = endPosition + 1
      })
    },

    cancelDescriptionChange () {
      this.descriptionModel = this.cardType.description || ''
      this.$refs.textareaElement.blur()
      this.closeDescription()
    },

    submitDescriptionChange (event) {
      this._emitUpdate({ description: this.descriptionModel })
      event.target.blur()
    },

    promptDeleteCardType (cardType) {
      this.typeToDelete = cardType
      this.showDeleteDialog = true
    },
    async confirmDeleteType () {
      if (this.typeToDelete) {
        this.isDeletingType = true
        const workflowId = this.$route.params.id
        try {
          await store.dispatch('cardTypes/deleteCardType', { id: this.typeToDelete.id, workflowId })
          await this.$store.dispatch('workflow/refreshCardTypes', { input: { workflowId: this.$route.params.id } })
          this.showDeleteDialog = false
          this.typeToDelete = null
        } finally {
          this.isDeletingType = false
        }
      }
    }
  }
}
</script>

<style scoped>
.card-type-rename-input {
  width: 100%;
}

.type-checkbox-column {
  justify-content: center;
}

.type-title {
  height: 28px;
  color: #1D1F22;
  font-size: 16px;
  text-align: left;
  line-height: 17.6px;
}

.show-hide-description {
  font-size: 13px;
  text-align: left;
  line-height: 14.3px;
}

.type-title-input-container input {
  min-width: 0;
  box-sizing: border-box;
  height: 28px;
  line-height: 28px;
  outline: none;
  border: none;
  border-bottom: 2px solid currentColor;
}

textarea {
  width: 100%;
  resize: none;
  outline: none;
  box-sizing: border-box;
  display: block;
  overflow: hidden;
  min-height: 40px;
}

.action-button {
  border-radius: 6px !important;
  min-width: 60px;
}

.expension-btn {
  cursor: pointer;
  user-select: none;
}

:deep(.v-select__menu-icon) {
  display: none !important;
}

.sortHandle {
  cursor: move;
  box-shadow: none !important;
  font-size: 16px !important;
}

.gap-x-4 {
  column-gap: 12px;
}

.eye-btn :deep(.v-btn__overlay) {
  background: none !important;
}
</style>
